// A person's relatives in words, for exports that list them: each with a short note on what
// kind of link it is ("adoptive", "married 1950–1980, divorced", "half", "best friend"), and
// how each person is related to the home person. Shared by the spreadsheet and web page.

import { dateYear } from '../model/dates';
import { graphOf, other, siblingsOf } from '../model/graph';
import { headlineRelations, relate } from '../model/kinship';
import { displayName, shortName } from '../model/people';
import type { Id, ParentType, PartnerRel, SiblingType, SocialType, Tree } from '../model/types';

export interface Linked {
  id: Id;
  /** What kind of link, when it isn't the plain one ("adoptive", "divorced"). */
  note?: string;
}

export interface Relatives {
  parents: Linked[];
  partners: Linked[];
  children: Linked[];
  siblings: Linked[];
  /** Friends, mentors, colleagues, and other social ties. */
  connections: Linked[];
}

const PARENT_WORDS: Partial<Record<ParentType, string>> = { adoptive: 'adoptive', step: 'step', foster: 'foster', guardian: 'guardian', chosen: 'chosen', surrogate: 'surrogate', donor: 'donor' };
const PARTNER_WORDS: Partial<Record<PartnerRel['type'], string>> = { married: 'married', 'civil-union': 'civil union', domestic: 'domestic partners', engaged: 'engaged', dating: 'dating' };
const STATUS_WORDS: Partial<Record<PartnerRel['status'], string>> = { separated: 'separated', divorced: 'divorced', ended: 'ended', widowed: 'widowed' };
const SIBLING_WORDS: Partial<Record<SiblingType, string>> = { half: 'half', adoptive: 'adoptive', step: 'step', foster: 'foster', chosen: 'chosen' };
const SOCIAL_WORDS: Record<SocialType, string> = { friend: 'friend', 'close-friend': 'close friend', 'best-friend': 'best friend', 'family-friend': 'family friend', mentor: 'mentor', roommate: 'roommate', colleague: 'colleague', neighbor: 'neighbor', other: '' };

/** "1950–1980", "1950", "until 1980", or "". */
export function yearRange(start?: string, end?: string): string {
  const a = dateYear(start);
  const b = dateYear(end);
  if (a !== undefined && b !== undefined) return a === b ? String(a) : `${a}–${b}`;
  if (a !== undefined) return String(a);
  if (b !== undefined) return `until ${b}`;
  return '';
}

/** "married 1950–1980, divorced", "partners", or "" for a current partnership with nothing else recorded. */
export function partnershipNote(r: PartnerRel): string {
  const what = r.label || PARTNER_WORDS[r.type] || '';
  return [[what, yearRange(r.start, r.end)].filter(Boolean).join(' '), STATUS_WORDS[r.status]].filter(Boolean).join(', ');
}

const note = (s: string | undefined): { note?: string } => (s ? { note: s } : {});

export function relativesOf(tree: Tree, id: Id): Relatives {
  const g = graphOf(tree);
  const parents = (g.parentRels.get(id) ?? []).map((r) => ({ id: r.a, ...note(r.label || PARENT_WORDS[r.type]) }));
  const children = (g.childRels.get(id) ?? []).map((r) => ({ id: r.b, ...note(r.label || PARENT_WORDS[r.type]) }));
  // A couple can have more than one partnership on record (married twice); list each.
  const partners = (g.partnerRels.get(id) ?? []).map((r) => ({ id: other(r, id), ...note(partnershipNote(r)) }));
  const siblings = siblingsOf(g, id).map((s) => ({ id: s.id, ...note(s.explicit?.label || SIBLING_WORDS[s.type]) }));
  const connections = (g.socialRels.get(id) ?? []).map((r) => {
    // Mentoring runs one way: a mentors b.
    const word = r.type === 'mentor' ? (r.a === id ? 'mentee' : 'mentor') : SOCIAL_WORDS[r.type];
    return { id: other(r, id), ...note(r.label || word) };
  });
  return { parents, partners, children, siblings, connections };
}

/** "Name" or "Name (note)". */
export function linkedText(tree: Tree, l: Linked): string {
  const name = displayName(tree.people[l.id]);
  return l.note ? `${name} (${l.note})` : name;
}

/**
 * How each person is related to the home person ("Ada’s first cousin"), worked out once per
 * person asked about. Undefined when the tree has no home person.
 */
export function homeRelations(tree: Tree): { home: Id; name: string; label: string; of: (id: Id) => string } | undefined {
  const home = tree.homePersonId;
  if (!home || !tree.people[home]) return undefined;
  const cache = new Map<Id, string>();
  const name = shortName(tree.people[home]);
  return {
    home,
    name,
    label: `Relationship to ${name}`,
    of: (id) => {
      if (id === home) return 'Home person';
      let term = cache.get(id);
      if (term === undefined) {
        term = headlineRelations(relate(tree, home, id))[0]?.term ?? '';
        cache.set(id, term);
      }
      return term;
    },
  };
}
